import React, { useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PRIORITY } from "@/lib/ui";
import { toast } from "sonner";

export default function CreateTicketDialog({ isClient, clients = [], engineers = [], onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("medium");
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [assignee, setAssignee] = useState("none");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setLoading(true);
    try {
      const body = { title, description: desc, priority };
      if (!isClient) {
        body.client_id = clientId || null;
        body.assignee_id = assignee === "none" ? null : assignee;
      }
      await api.post("/tickets", body);
      toast.success("Ticket raised");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not raise ticket");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-ticket-dialog">
        <DialogHeader><DialogTitle className="font-display">Raise a ticket</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary of the issue" autoFocus data-testid="ticket-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What's happening? Steps to reproduce…" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!isClient && (
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger data-testid="ticket-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {!isClient && (
              <div className="space-y-2">
                <Label>Assign engineer</Label>
                <Select value={assignee} onValueChange={setAssignee}>
                  <SelectTrigger data-testid="ticket-assignee-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {engineers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ticket-create-submit">
            {loading ? "Submitting…" : "Submit ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
